import type { SerializeFrom } from '~/types/serialize-from';
import { useMatches } from 'react-router';
import { useMemo } from 'react';
import type {
    PageBuilderModuleType,
    TopBanner,
} from '@modules/page-builder/types';
import type { loader } from '~/routes/_app.project.$urlSegment._index';

const PROJECT_ROUTE = `routes/_app.project.$urlSegment._index`;
const PREVIEW_ROUTE = `routes/_app.project.$urlSegment.template.$url`;
/**
 * The Business Manager page builder renders the revamp's chrome around the
 * editable modules, so its sections resolve their project through here too.
 * Matching is by route id, so omitting this one leaves `combinedData.project`
 * undefined and the spread below throws rather than degrading.
 */
const EDIT_ROUTE = `routes/_app.project.$urlSegment.edit.$`;

interface TemplateData {
    Banner: TopBanner;
    Modules: PageBuilderModuleType[];
}

interface CombinedDataType extends SerializeFrom<typeof loader> {
    templateData?: TemplateData;
}

export const useProjectPreviewRouteData = () => {
    const matchingRoutes = useMatches();

    const combinedData = useMemo(() => {
        const filteredRoutes = matchingRoutes.filter((route) => {
            return [PROJECT_ROUTE, PREVIEW_ROUTE, EDIT_ROUTE].includes(
                route.id,
            );
        });

        return filteredRoutes.reduce((result, item) => {
            Object.assign(result, item.data);
            return result;
        }, {} as CombinedDataType);
    }, [matchingRoutes]);

    const { project, templateData } = combinedData;

    return {
        ...project,
        Banner: templateData?.Banner ?? project.Banner,
        Modules: templateData?.Modules ?? project.Modules,
    };
};
